"use client";

import type { ComponentProps } from "react";
import * as React from "react";
import NextImage from "next/image";

import { cn } from "@/lib/utils";

/**
 * Props for the `Image` component. Extends the props from `NextImage`.
 */
export type ImageProps = ComponentProps<typeof NextImage>;

/**
 * `Image` component that wraps around Next.js's `Image` component with additional
 * styles and loading state handling.
 *
 * @param {ImageProps} props - The properties to pass to the `NextImage` component.
 * @param {string} [props.className] - Optional additional CSS class names.
 * @param {string} [props.alt=''] - Alternative text for the image. Defaults to an empty string.
 * @param {boolean} [props.priority=false] - Whether to prioritize loading this image.
 * @param {'lazy'|'eager'} [props.loading='lazy'] - Loading strategy for the image.
 *
 * @example
 * ```tsx
 * <Image
 *   src="/path/to/image.jpg"
 *   alt="Description of image"
 *   className="my-custom-class"
 *   loading="lazy"
 * />
 * ```
 *
 * The component displays a loading state with a blur effect until the image is fully loaded.
 * By default, images are lazy loaded for better performance.
 *
 * @returns {JSX.Element} The rendered `NextImage` component with applied styles and loading state.
 */
export function Image({
  className,
  src,
  alt = "",
  priority = false,
  loading = "lazy",
  ...props
}: ImageProps) {
  const [isLoading, setLoading] = React.useState(true);

  return (
    <NextImage
      alt={alt}
      className={cn(
        className,
        "select-none duration-700 ease-in-out",
        isLoading ? "blur-[2px]" : "blur-0"
      )}
      onLoad={() => setLoading(false)}
      src={src ?? ""}
      priority={priority}
      loading={loading}
      // Optimize image formats
      quality={85}
      // width={500}
      // height={500}
      {...props}
    />
  );
}
